import React, { useState } from 'react';
import {
  Bell,
  CalendarDays,
  CalendarRange,
  Clock3,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  Rss,
  Server,
  Shield,
  UserCog,
  X,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import type { Me } from '../App';
import { api } from '../api';
import { useToast } from '../ui/Toast';

interface Props {
  me: Me;
  onLogout: () => void;
  children: React.ReactNode;
}

const nav = [
  { to: '/', label: 'Dashboard', group: 'Workspace', icon: LayoutDashboard },
  { to: '/calendar', label: 'Kalenderansicht', group: 'Workspace', icon: CalendarDays },
  { to: '/calendars', label: 'Kalender', group: 'Workspace', icon: CalendarRange },
  { to: '/events', label: 'Termine', group: 'Workspace', icon: ListChecks },
  { to: '/subscriptions', label: 'Abos', group: 'Workspace', icon: Rss },
  { to: '/freebusy', label: 'Free/Busy', group: 'Workspace', icon: Clock3 },
  { to: '/notifications', label: 'Benachrichtigungen', group: 'Workspace', icon: Bell },
  { to: '/settings', label: 'Profil & Sicherheit', group: 'Konto', icon: UserCog },
  { to: '/caldav-info', label: 'CalDAV-Zugang', group: 'Konto', icon: Server },
  { to: '/app-passwords', label: 'App-Passwoerter', group: 'Konto', icon: KeyRound },
];

const mobilePrimaryNav = [
  { to: '/calendar', label: 'Kalender', icon: CalendarDays },
  { to: '/events', label: 'Termine', icon: ListChecks },
  { to: '/notifications', label: 'Inbox', icon: Bell },
  { to: '/settings', label: 'Konto', icon: UserCog },
];

export function Layout({ me, onLogout, children }: Props) {
  const toast = useToast();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const groups = Array.from(new Set(nav.map((n) => n.group)));
  const adminItems = me.role === 'ADMIN' ? [{ to: '/admin', label: 'Admin', group: 'Admin', icon: Shield }] : [];
  const allItems = [...nav, ...adminItems];

  const handleLogout = async () => {
    await api.post('/api/auth/logout');
    toast.success('Du bist abgemeldet.');
    onLogout();
  };

  return (
    <div className="min-h-screen flex bg-bg">
      {mobileNavOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileNavOpen(false)} />}
      <aside className={`fixed lg:sticky inset-y-0 left-0 z-50 w-[260px] shrink-0 bg-surface-sidebar border-r border-border flex flex-col transition-transform duration-slide ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="h-[72px] px-5 flex items-center gap-3 border-b border-border">
          <div className="w-7 h-7 rounded-md bg-brand-gradient flex items-center justify-center text-white font-bold text-meta">FC</div>
          <div>
            <div className="text-body font-semibold leading-none">
              <span className="font-bold">bbf</span><span className="font-normal">design</span>
            </div>
            <div className="text-meta uppercase tracking-[0.16em] text-ink-muted mt-1">ForgeCalendar</div>
          </div>
          <button className="btn btn-ghost !px-2 ml-auto lg:hidden" onClick={() => setMobileNavOpen(false)} title="Navigation schliessen"><X size={18} /></button>
        </div>

        <nav className="flex-1 px-3 py-2 overflow-y-auto">
          {[...groups, ...(me.role === 'ADMIN' ? ['Admin'] : [])].map((g) => (
            <div key={g}>
              <div className="section-label">{g}</div>
              {allItems
                .filter((i) => i.group === g)
                .map((i) => {
                  const Icon = i.icon;
                  return (
                    <NavLink
                      key={i.to}
                      to={i.to}
                      end={i.to === '/'}
                      onClick={() => setMobileNavOpen(false)}
                      className={({ isActive }) => `nav-item ${isActive ? 'nav-item-active' : ''}`}
                    >
                      <Icon size={17} className="shrink-0 opacity-80" />
                      <span className="truncate">{i.label}</span>
                    </NavLink>
                  );
                })}
            </div>
          ))}
        </nav>

        <div className="h-[64px] border-t border-border px-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-surface-elevated flex items-center justify-center text-ink-primary font-semibold text-small">
            {(me.name ?? me.email).slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-body text-ink-primary truncate">{me.name ?? me.email}</div>
            <div className="text-meta text-ink-muted">{me.role}</div>
          </div>
          <button onClick={handleLogout} className="btn btn-ghost !px-2" title="Abmelden"><LogOut size={17} /></button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-[72px] sticky top-0 z-30 bg-bg/80 backdrop-blur-[14px] border-b border-border flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button className="btn btn-secondary !px-3 lg:hidden" onClick={() => setMobileNavOpen(true)} title="Navigation oeffnen"><Menu size={18} /></button>
            <div className="min-w-0">
              <div className="text-h4 truncate">ForgeCalendar</div>
              <div className="text-meta text-ink-muted lg:hidden truncate">{labelForPath(location.pathname)}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="badge badge-info font-mono hidden sm:inline-flex">build {import.meta.env.MODE === 'development' ? 'dev' : '0.1.0'}</span>
            <span className="badge badge-muted max-w-[180px] truncate">{me.email}</span>
          </div>
        </header>
        <main className="flex-1 p-3 pb-24 sm:p-5 lg:p-6 overflow-y-auto">{children}</main>
      </div>
      <nav className="fixed bottom-0 left-0 right-0 z-30 grid grid-cols-4 border-t border-border bg-bg/95 backdrop-blur-[14px] lg:hidden">
        {mobilePrimaryNav.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `grid gap-1 py-2.5 place-items-center text-small ${isActive ? 'text-ink-primary bg-brand-primary/15' : 'text-ink-muted'}`}
            >
              <Icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

function labelForPath(pathname: string): string {
  const item = nav.find((n) => (n.to === '/' ? pathname === '/' : pathname.startsWith(n.to)));
  return item?.label ?? 'Workspace';
}
